import { lazy } from "react";
import type { RouteObject } from "react-router-dom";

import RedirectPageRoute from "~/pages/RedirectPageRoute";

// Template components - lazy loaded for better code splitting
const LeaseListingTemplate = lazy(
  () => import("~/templates/lease/LeaseListingTemplate")
);
const MaintenanceListingTemplate = lazy(
  () => import("~/templates/maintanceRequest/MaintenanceListingTemplate")
);

const LayoutLandlord = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/LayoutLandlord")
);
const LayoutAccounts = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/accounts/LayoutAccounts")
);
const LayoutAllAccounts = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/accounts/withLayout/LayoutAccounts")
);
const LayoutExpenses = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/expenses/LayoutExpenses")
);
const LayoutLeaseListing = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/leases/listing/LayoutLeaseListing")
);
const LayoutLeaseId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/leases/leaseId/LayoutLeaseId")
);
const LayoutMaintanceListing = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/maintenance/listing/LayoutMaintanceListing")
);
const LayoutMaintenanceItem = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/maintenance/requestId/LayoutMaintenanceItem")
);
const LayoutPeopleRoot = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/people/root/LayoutPeopleRoot")
);
const LayoutContactId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/people/contacts/LayoutContactId")
);
const LayoutTenantId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/people/tenants/LayoutTenantId")
);
const LayoutVendorId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/people/vendors/LayoutVendorId")
);
const LayoutProperty = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/properties/LayoutProperty")
);
const LayoutUnitId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/properties/units/LayoutUnitId")
);
const LayoutRecurringInvoiceId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/recurringInvoices/LayoutRecurringInvoiceId")
);
const LayoutReport = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/reports/LayoutReport")
);
const LayoutTransactions = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/transactions/LayoutTransactions")
);

// Lazy load main pages
const PageLandlord = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/PageLandlord")
);
const PageMarketing = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/marketing/PageMarketing")
);
const PageMarketingUnit = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/marketing/PageMarketingUnit")
);

// File View Pages
const PageContactFile = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/fileView/PageContactFile")
);
const PageDocumentFile = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/fileView/PageDocumentFile")
);
const PageLeaseFile = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/fileView/PageLeaseFile")
);
const PagePropertyFile = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/fileView/PagePropertyFile")
);
const PageTenantFile = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/fileView/PageTenantFile")
);
const PageUnitFile = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/fileView/PageUnitFile")
);
const PageVendorFile = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/fileView/PageVendorFile")
);

// General Pages
const PageAccountSetup = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/PageAccountSetup")
);

// Account Management Pages
const PageAccountIds = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/accounts/PageAccountId")
);
const PageStripeOnboarding = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/accounts/PageStripeOnboarding")
);
const PageAllAccounts = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/accounts/withLayout/PageAccounts")
);
const PageAccountSettings = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/accounts/withLayout/PageSettings")
);

// Condition Reports
const PageConditionReport = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/conditionReports/PageConditionReport")
);

// Document Management Pages
const PageDocuments = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/documents/PageDocuments")
);
const LayoutDocumentSigning = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/documents/LayoutDocumentSigning")
);
const LayoutDocument = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/documents/LayoutDocument")
);
const PageDocumentId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/documents/documentId/PageDocumentId")
);
const PageEditDocument = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/documents/documentId/PageEditDocument")
);
const PageRequestSignature = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/documents/documentId/PageRequestSignature")
);
const PageRequestSignatureUpload = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/documents/request-signature/PageRequestSignatureUpload")
);
const PageDocumentSign = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/documents/documentId/PageDocumentSign")
);

// Expenses
const PageExpenses = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/expenses/PageExpenses")
);

// Forms
const PageLandlordTemplates = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/documents/PageTemplates")
);

const PageWizardDynamic = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/wizards/PageWizardDynamic")
);

// Leases
const PageleaseId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/leases/leaseId/PageLeaseId")
);
const PageLeaseConditionReports = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/leases/leaseId/tabs/PageLeaseConditionReports")
);
const PageLeaseMaintenance = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/leases/leaseId/tabs/PageLeaseMaintenance")
);
const PageLeaseNotes = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/leases/leaseId/tabs/PageLeaseNotes")
);
const PageLeaseRequiredDocuments = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/leases/leaseId/tabs/PageLeaseRequiredDocuments")
);
const PageLeaseSecurityDeposit = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/leases/leaseId/tabs/PageLeaseSecurityDeposit")
);
const PageLeaseTenants = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/leases/leaseId/tabs/PageLeaseTenants")
);
const PageLeaseTransactions = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/leases/leaseId/tabs/PageLeaseTransactions")
);

// Maintenance Requests
const PageMaintenanceListing = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/maintenance/listing/PageMaintenanceListing")
);
const PageMaintenanceItem = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/maintenance/requestId/PageMaintenanceItem")
);

// People
const PagePeopleRoot = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/people/root/PagePeopleRoot")
);
const PageContactRoot = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/people/root/PageContactRoot")
);
const PageVendorRoot = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/people/root/PageVendorRoot")
);
const PageContactId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/people/contacts/PageContactId")
);
const PageContactIdFiles = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/people/contacts/PageContactIdFiles")
);
const PageLeads = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/people/leads/PageLeads")
);
const LayoutLeadId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/people/leads/LayoutLeadId")
);
const PageLeadId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/people/leads/PageLeadId")
);
const PageTenantId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/people/tenants/PageTenantId")
);
const PageTenantIdFiles = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/people/tenants/PageTenantIdFiles")
);
const PageTenantIdLeases = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/people/tenants/PageTenantIdLeases")
);
const PageVendorId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/people/vendors/PageVendorId")
);
const PageVendorIdFiles = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/people/vendors/PageVendorIdFiles")
);

// Properties
const PagePropertyId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/properties/PagePropertyId")
);
const PagePropertyConditionReports = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/properties/PagePropertyConditionReports")
);
const PagePropertyFiles = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/properties/PagePropertyFiles")
);
const PagePropertyLeases = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/properties/PagePropertyLeases")
);
const PagePropertyMaintenance = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/properties/PagePropertyMaintenance")
);
const PagePropertyNotes = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/properties/PagePropertyNotes")
);
const PagePropertySecurityDeposit = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/properties/PagePropertySecurityDeposit")
);
const PagePropertyTransactions = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/properties/PagePropertyTransactions")
);

// Units
const PageUnitConditionReports = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/properties/units/PageUnitConditionReports")
);
const PageUnitFiles = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/properties/units/PageUnitFiles")
);
const PageUnitLeases = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/properties/units/PageUnitLeases")
);
const PageUnitMaintenance = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/properties/units/PageUnitMaintenance")
);

const PageTenantMaintenanceRequestIdComments = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/maintenance/requestId/PageMaintenanceRequestIdComments")
);

const PageUnitInfo = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/properties/units/PageUnitInfo")
);
const PageUnitNotes = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/properties/units/PageUnitNotes")
);
const PageUnitSecurityDeposits = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/properties/units/PageUnitSecurityDeposits")
);
const PageUnitTransactions = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/properties/units/PageUnitTransactions")
);

// Recurring Invoices
const PageRecurringInvoiceId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/recurringInvoices/PageRecurringInvoiceId")
);

// Reports
const PageReport = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/reports/PageReport")
);
const PageReportExpense = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/reports/PageReportExpense")
);
const PageReportIncomeStatement = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/reports/PageReportIncomeStatement")
);
const PageReportRentRoll = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/reports/PageReportRentRoll")
);

// Transactions
const PageTransactions = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/transactions/PageTransactions")
);
const PageTransactionsPaidInvoices = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/transactions/PageTransactionsPaidInvoices")
);
const PageTransactionsRecurringPayments = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/transactions/PageTransactionsRecurringPayments")
);
const PageTransactionsSecurityDeposits = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/landlord/general/transactions/PageTransactionsSecurityDeposits")
);

export const landlordRoutes: RouteObject = {
  path: "/landlord",
  Component: LayoutLandlord,
  children: [
    // Landlord Dashboard
    { index: true, Component: PageLandlord },

    // Marketing
    {
      path: "marketing",
      children: [
        { index: true, Component: PageMarketing },
        { path: "property-listing/:unitId", Component: PageMarketingUnit },
      ],
    },

    // ====================================================
    // FILE VIEW ROUTES
    // ====================================================
    {
      path: "documents/files/:fileId",
      Component: PageDocumentFile,
    },
    {
      path: "leases/:leaseId/files/:fileId",
      Component: PageLeaseFile,
    },
    {
      path: "people/contacts/:contactId/files/:fileId",
      Component: PageContactFile,
    },
    {
      path: "people/tenants/:tenantId/files/:fileId",
      Component: PageTenantFile,
    },
    {
      path: "people/vendors/:vendorId/files/:fileId",
      Component: PageVendorFile,
    },
    {
      path: "properties/:propertyId/files/:fileId",
      Component: PagePropertyFile,
    },
    {
      path: "properties/:propertyId/units/:unitId/files/:fileId",
      Component: PageUnitFile,
    },

    // ====================================================
    // GENERAL LANDLORD ROUTES
    // ====================================================

    // Account Setup
    {
      path: "account-setup",
      Component: PageAccountSetup,
    },

    // Account Management
    {
      path: "accounts",
      Component: LayoutAccounts,
      children: [
        {
          Component: LayoutAllAccounts,
          children: [
            {
              index: true,
              Component: PageAllAccounts,
            },
            {
              path: "settings",
              Component: PageAccountSettings,
            },
          ],
        },
        {
          path: ":accountId",
          children: [
            {
              index: true,
              Component: PageAccountIds,
            },
            {
              path: "stripe-onboarding",
              Component: PageStripeOnboarding,
            },
          ],
        },
      ],
    },

    // Condition Reports
    {
      path: "condition-reports/:conditionReportId",
      Component: PageConditionReport,
    },

    // Document Management
    {
      path: "documents",
      Component: LayoutDocument,
      children: [
        {
          Component: LayoutDocumentSigning,
          children: [
            {
              index: true,
              Component: PageDocuments,
            },
            {
              path: "templates",
              Component: PageLandlordTemplates,
            },
          ],
        },
        {
          path: ":documentId",
          Component: PageDocumentId,
        },
        {
          path: ":documentId/edit",
          Component: PageEditDocument,
        },
        {
          path: ":documentId/request-signature",
          Component: PageRequestSignature,
        },
        {
          path: ":documentId/sign",
          Component: PageDocumentSign,
        },
        {
          path: "request-signature",
          Component: PageRequestSignatureUpload,
        },
      ],
    },

    // Expenses
    {
      path: "expenses",
      Component: LayoutExpenses,
      children: [
        {
          index: true,
          Component: PageExpenses,
        },
      ],
    },

    // Wizards
    {
      path: "wizards",
      element: <RedirectPageRoute to="/landlord/" />,
    },
    {
      path: "wizards/:wizardSlug",
      Component: PageWizardDynamic,
    },

    // Leases
    {
      path: "leases",
      children: [
        {
          Component: LayoutLeaseListing,
          children: [
            {
              index: true,
              Component: LeaseListingTemplate,
            },
            {
              path: "draft",
              Component: () => <LeaseListingTemplate status="draft" />,
            },
            {
              path: "past",
              Component: () => <LeaseListingTemplate status="past" />,
            },
            {
              path: "upcoming",
              Component: () => <LeaseListingTemplate status="upcoming" />,
            },
          ],
        },
        {
          path: ":leaseId",
          Component: LayoutLeaseId,
          children: [
            {
              index: true,
              Component: PageleaseId,
            },
            {
              path: "condition-reports",
              Component: PageLeaseConditionReports,
            },
            {
              path: "files",
              element: <RedirectPageRoute to="/landlord/leases/:leaseId" />,
            },
            {
              path: "maintenance",
              Component: PageLeaseMaintenance,
            },
            {
              path: "notes",
              Component: PageLeaseNotes,
            },
            {
              path: "required-documents",
              Component: PageLeaseRequiredDocuments,
            },
            {
              path: "security-deposit",
              Component: PageLeaseSecurityDeposit,
            },
            {
              path: "tenants",
              Component: PageLeaseTenants,
            },
            {
              path: "transactions",
              Component: PageLeaseTransactions,
            },
          ],
        },
      ],
    },
    // Maintenance Requests
    {
      path: "maintenance",
      children: [
        {
          Component: LayoutMaintanceListing,
          children: [
            {
              index: true,
              Component: PageMaintenanceListing,
            },
            {
              path: "completed",
              Component: () => <MaintenanceListingTemplate status="complete" />,
            },
          ],
        },
      ],
    },
    {
      path: "maintenance/:requestId",
      Component: LayoutMaintenanceItem,
      children: [
        {
          index: true,
          Component: PageMaintenanceItem,
        },
        {
          path: "comments",
          Component: PageTenantMaintenanceRequestIdComments,
        },
      ],
    },
    // People
    {
      path: "people",
      children: [
        {
          Component: LayoutPeopleRoot,
          children: [
            {
              index: true,
              Component: PagePeopleRoot,
            },
            {
              path: "contacts",
              Component: PageContactRoot,
            },
            {
              path: "vendors",
              Component: PageVendorRoot,
            },
          ],
        },
        {
          path: "contacts/:contactId",
          Component: LayoutContactId,
          children: [
            {
              index: true,
              Component: PageContactId,
            },
            {
              path: "files",
              Component: PageContactIdFiles,
            },
          ],
        },
        {
          path: "leads",
          Component: PageLeads,
        },
        {
          path: "leads/:leadId",
          Component: LayoutLeadId,
          children: [
            {
              index: true,
              Component: PageLeadId,
            },
          ],
        },
        {
          path: "tenants",
          element: <RedirectPageRoute to="/landlord/people" />,
        },
        {
          path: "tenants/:tenantId",
          Component: LayoutTenantId,
          children: [
            {
              index: true,
              Component: PageTenantId,
            },
            {
              path: "files",
              Component: PageTenantIdFiles,
            },
            {
              path: "leases",
              Component: PageTenantIdLeases,
            },
          ],
        },
        {
          path: "vendors/:vendorId",
          Component: LayoutVendorId,
          children: [
            {
              index: true,
              Component: PageVendorId,
            },
            {
              path: "files",
              Component: PageVendorIdFiles,
            },
          ],
        },
      ],
    },
    {
      path: "properties",
      element: <RedirectPageRoute to="/landlord" />,
    },
    {
      path: "properties/:propertyId",
      children: [
        {
          Component: LayoutProperty,
          children: [
            {
              index: true,
              Component: PagePropertyId,
            },
            {
              path: "units",
              element: (
                <RedirectPageRoute to="/landlord/properties/:propertyId" />
              ),
            },
            {
              path: "condition-reports",
              Component: PagePropertyConditionReports,
            },
            {
              path: "files",
              Component: PagePropertyFiles,
            },
            {
              path: "leases",
              Component: PagePropertyLeases,
            },
            {
              path: "maintenance",
              Component: PagePropertyMaintenance,
            },
            {
              path: "notes",
              Component: PagePropertyNotes,
            },
            {
              path: "security-deposits",
              Component: PagePropertySecurityDeposit,
            },
            {
              path: "transactions",
              Component: PagePropertyTransactions,
            },
          ],
        },
      ],
    },
    {
      path: "properties/:propertyId/units/:unitId",
      Component: LayoutUnitId,
      children: [
        {
          index: true,
          Component: PageUnitInfo,
        },
        {
          path: "notes",
          Component: PageUnitNotes,
        },
        {
          path: "condition-reports",
          Component: PageUnitConditionReports,
        },
        {
          path: "files",
          Component: PageUnitFiles,
        },
        {
          path: "leases",
          Component: PageUnitLeases,
        },
        {
          path: "maintenance",
          Component: PageUnitMaintenance,
        },
        {
          path: "security-deposits",
          Component: PageUnitSecurityDeposits,
        },
        {
          path: "transactions",
          Component: PageUnitTransactions,
        },
      ],
    },
    {
      path: "recurring-invoices",
      element: <RedirectPageRoute to="/transactions/recurring-invoices" />,
    },
    {
      path: "recurring-invoices/:recurringInvoiceId",
      Component: LayoutRecurringInvoiceId,
      children: [
        {
          index: true,
          Component: PageRecurringInvoiceId,
        },
      ],
    },
    {
      path: "reports",
      Component: LayoutReport,
      children: [
        {
          index: true,
          Component: PageReport,
        },
        {
          path: "expense-report",
          Component: PageReportExpense,
        },
        {
          path: "income-statement",
          Component: PageReportIncomeStatement,
        },
        {
          path: "rent-roll",
          Component: PageReportRentRoll,
        },
      ],
    },
    {
      path: "transactions",
      Component: LayoutTransactions,
      children: [
        {
          index: true,
          Component: PageTransactions,
        },
        {
          path: "paid-invoices",
          Component: PageTransactionsPaidInvoices,
        },
        {
          path: "recurring-payments",
          Component: PageTransactionsRecurringPayments,
        },
        {
          path: "security-deposits",
          Component: PageTransactionsSecurityDeposits,
        },
      ],
    },
  ],
};
